<template>
  <div
    v-if="canView"
    class="to-complete-section"
  >
    <div
      v-if="loading"
      class="to-complete-section__progress-bar"
    />

    <MonitoringFilterBar
      v-model="draftFilters"
      :fields="fields"
      :loading="loading"
      :can-export="canExport"
      @refresh="search"
      @clear="clearFilters"
      @export="debouncedExport"
    />

    <el-alert
      v-if="cardsError"
      class="to-complete-section__error"
      type="error"
      :title="cardsError"
      :closable="false"
      show-icon
    />
    <MonitoringStatCardBlock
      v-else
      :definition="kpiDef"
      :data="kpis"
      urgency="warning"
      :loading="cardsLoading"
      is-static
    />

    <ToCompleteTable
      :filters="appliedFilters"
      :loading.sync="tableLoading"
      @criteria-change="onCriteriaChange"
      @order-change="onOrderChange"
    />
  </div>
</template>

<script>
  import axios from 'axios';
  import { mapGetters, mapActions } from 'vuex';
  import debounce from 'lodash.debounce';
  import fileSaver from 'file-saver';
  import moment from 'moment';
  import { monitoringApi } from '@/api';
  import acl from '@/services/acl';
  import { laboratories } from '@/config/constants';
  import {
    TO_COMPLETE_FILTER_KEY,
    TO_COMPLETE_I18N_BASE,
    locationAutocomplete,
    toCompleteColumnFilters,
  } from '@/config/monitoringToComplete.config';
  import {
    STABILITY_FILTER_DEFAULT,
    STABILITY_FILTER_OPTIONS,
  } from '@/config/stabilityFilter';
  import StabilityFilter from '@/components/StabilityFilter.vue';
  import MonitoringFilterBar from './MonitoringFilterBar.vue';
  import MonitoringStatCardBlock from './MonitoringStatCardBlock.vue';
  import ToCompleteTable from './ToCompleteTable.vue';

  const TO_COMPLETE_PERMISSIONS = ['dashboardMonitoringView'];
  const ANALYSIS_PERMISSIONS = ['analysesDataView', 'analysesStatusView'];

  const MANDATORY_FIELDS = [
    {
      name: 'acceptedAt',
      label: `${TO_COMPLETE_I18N_BASE}.acceptedAt`,
      type: 'dateRange',
      mandatory: true,
      required: true,
      span: 8,
    },
    {
      name: 'locationId',
      label: `${TO_COMPLETE_I18N_BASE}.location`,
      type: 'autocomplete',
      placeholder: 'monitoringDashboard.filters.sedePlaceholder',
      autocompleteSearch: locationAutocomplete,
      mandatory: true,
      span: 6,
    },
    {
      name: 'laboratory',
      label: `${TO_COMPLETE_I18N_BASE}.laboratory`,
      type: 'select',
      placeholder: 'monitoringDashboard.filters.repartoPlaceholder',
      options: [
        { value: laboratories.CHIM, label: 'enums.laboratories.CHIM' },
        { value: laboratories.MICR, label: 'enums.laboratories.MICR' },
      ],
      mandatory: true,
      span: 6,
    },
    {
      name: 'showParents',
      label: 'monitoringDashboard.toComplete.filters.showParents',
      type: 'toggle',
      mandatory: true,
      span: 4,
    },
    {
      name: 'showUnsignedRevisions',
      label: 'monitoringDashboard.toComplete.filters.showUnsignedRevisions',
      type: 'toggle',
      mandatory: true,
      span: 6,
    },
    {
      name: 'stabilityFilter',
      label: 'monitoringDashboard.toComplete.filters.stability.label',
      type: 'exclusiveSelect',
      component: StabilityFilter,
      options: STABILITY_FILTER_OPTIONS,
      defaultValue: STABILITY_FILTER_DEFAULT,
      mandatory: true,
      span: 6,
    },
  ];

  const COLUMN_FIELDS = toCompleteColumnFilters.map((filter) => ({
    name: filter.name,
    label: `${TO_COMPLETE_I18N_BASE}.${filter.label}`,
    type: filter.type,
    options: filter.options,
    placeholder: filter.placeholder,
    autocompleteSearch: filter.autocompleteSearch,
    operators:
      filter.operators &&
      filter.operators.map((operator) => ({
        value: operator,
        label: `monitoringDashboard.toComplete.filters.operators.${operator}`,
      })),
    span: filter.span || 6,
  }));

  function emptyFilters() {
    return {
      acceptedAt: null,
      locationId: [],
      laboratory: [],
      showParents: false,
      showUnsignedRevisions: false,
      stabilityFilter: STABILITY_FILTER_DEFAULT,
    };
  }

  export default {
    name: 'ToCompleteSection',
    components: {
      MonitoringFilterBar,
      MonitoringStatCardBlock,
      ToCompleteTable,
    },
    data() {
      return {
        // editing a filter without pressing Aggiorna changes nothing on screen.
        draftFilters: emptyFilters(),
        appliedFilters: null,
        fields: [...MANDATORY_FIELDS, ...COLUMN_FIELDS],
        kpis: {},
        // The table owns the `where`: the cards and the export read the
        // criteria it emits rather than building a second one.
        criteria: null,
        order: null,
        cardsLoading: false,
        tableLoading: false,
        cardsError: null,
        summaryAbortController: null,
        debouncedExport: null,
      };
    },
    created() {
      this.debouncedExport = debounce(this.exportExcel, 300);
      this.hydrateFilters();
      this.search();
    },
    beforeDestroy() {
      this.debouncedExport.cancel();
      this.abortSummary();
    },
    computed: {
      ...mapGetters(['filtersStatus']),
      canView() {
        return (
          acl.userCanUse(TO_COMPLETE_PERMISSIONS) &&
          acl.userCanUse(ANALYSIS_PERMISSIONS)
        );
      },
      canExport() {
        return !!this.criteria;
      },
      loading() {
        return this.cardsLoading || this.tableLoading;
      },
      kpiDef() {
        return {
          blockKey: 'toComplete',
          titleKey: 'monitoringDashboard.toComplete.card.title',
          cards: [
            {
              key: 'totalSamples',
              dataKey: 'totalSamples',
              labelKey: 'monitoringDashboard.toComplete.card.total',
              icon: 'el-icon-files',
              severity: 'info',
            },
            {
              key: 'expiredSamples',
              dataKey: 'expiredSamples',
              labelKey: 'monitoringDashboard.toComplete.card.expired',
              icon: 'el-icon-warning',
              severity: 'critical',
            },
          ],
        };
      },
    },
    methods: {
      ...mapActions(['applyFilters', 'resetFilters']),
      hydrateFilters() {
        const stored = this.filtersStatus[TO_COMPLETE_FILTER_KEY] || {};
        this.draftFilters = { ...emptyFilters(), ...stored };
      },
      search() {
        this.appliedFilters = { ...this.draftFilters };
        this.applyFilters({
          filterKey: TO_COMPLETE_FILTER_KEY,
          filterData: this.appliedFilters,
        });
      },
      // Every filter change reaches the cards through here, column filters
      // included: they move the `where`, so they move the totals too.
      onOrderChange(order) {
        this.order = order;
      },
      onCriteriaChange(criteria) {
        this.criteria = criteria;
        if (!criteria) {
          this.abortSummary();
          this.cardsError = null;
          this.kpis = {};
          return;
        }
        this.loadSummary();
      },
      abortSummary() {
        if (this.summaryAbortController) this.summaryAbortController.abort();
        this.summaryAbortController = null;
      },
      async loadSummary() {
        this.abortSummary();
        this.summaryAbortController = new AbortController();
        const { signal } = this.summaryAbortController;
        this.cardsError = null;
        this.cardsLoading = true;
        let aborted = false;
        try {
          const { data } = await monitoringApi.toCompleteSummary(
            this.criteria,
            { signal }
          );
          this.kpis = data;
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.kpis = {};
          this.cardsError = this.errorMessage(error);
        } finally {
          if (!aborted) this.cardsLoading = false;
        }
      },
      clearFilters() {
        this.draftFilters = emptyFilters();
        this.appliedFilters = null;
        this.resetFilters(TO_COMPLETE_FILTER_KEY);
      },
      // The criteria without skip/limit, so the file holds the whole filtered
      // set and not the visible page. The sort goes as the top-level `order`
      // param the export endpoint declares; sending it inside `filter` instead
      // would put a second, competing source next to the top-level `where`.
      exportParams() {
        if (!this.order) return this.criteria;
        return { ...this.criteria, order: [this.order] };
      },
      async exportExcel() {
        if (!this.criteria) return;
        const loading = this.$loading({
          lock: true,
          background: 'rgba(48,53,82,0.9)',
        });
        try {
          const response = await monitoringApi.toCompleteExport(
            this.exportParams()
          );
          fileSaver.saveAs(
            response.data,
            `da-completare-${moment().format('YYYY-MM-DD')}.xlsx`
          );
        } catch (error) {
          this.notifyError(error);
        } finally {
          loading.close();
        }
      },
      errorMessage(error) {
        return (
          (error.response &&
            error.response.data &&
            error.response.data.error &&
            error.response.data.error.message) ||
          error.message ||
          this.$t('global.error')
        );
      },
      notifyError(error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: this.errorMessage(error),
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .to-complete-section {
    width: 100%;
    position: relative;

    &__error {
      margin-bottom: 12px;
    }

    &__progress-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(
        90deg,
        $--color-primary 0%,
        $--color-primary 50%,
        transparent 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: toCompleteProgressBar 1.5s linear infinite;
      z-index: 9999;
    }
  }

  @keyframes toCompleteProgressBar {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: 200% 0%;
    }
  }
</style>
